import {
	CustomEventUser,
	LoginButtonProps,
	LoginButtonWidgetLanguages,
	LoginButtonWidgetProps,
	LoginProfileWidgetProps,
	MSRCLoginData,
} from 'components/molecules/LoginButton/LoginButton.types';
import appConfig from 'helper/mapBackendConfig';
import * as React from 'react';
import { migrosLoginStatuses } from 'store/login';

/* tslint:disable */
declare global {
	interface Window {
		msrc: any;
	}
}
/* tslint:enable */

export type LoginWidgetState = {
	loginWidgetUID: string;
	loginButtonInitialized: boolean;
};

export class LoginButtonWeb extends React.Component<LoginButtonProps, LoginWidgetState> {
	public static displayName = 'LoginButton';
	public static defaultProps = {
		showUserData: false,
		setupLoginFlow: false,
		bigButton: false,
		isMigrosAuthenticated: false,
		showLoginButton: false,
	};

	constructor(props: LoginButtonProps) {
		super(props);

		this.state = {
			// a unique ID if there is more than one login button on the page
			loginWidgetUID: 'uid-' + Math.floor(Math.random() * Math.floor(99999999999)),
			loginButtonInitialized: false,
		};
	}

	public initLoginButton() {
		const {
			bigButton,
			migrosLoginReturnToURL,
			migrosLogoutReturnToURL,
			showLoginButton,
		}: LoginButtonProps = this.props;
		const language: LoginButtonWidgetLanguages = appConfig.env.language as LoginButtonWidgetLanguages;
		const loginProfileOptions: LoginProfileWidgetProps = {
			language,
			theme: 'gesundheitsplattform',
			mode: 'default',
			size: bigButton ? 'large' : 'small',
			headerHeight: { mobile: '52px' },
			inlinks: {
				account: appConfig.env.msrcConfig.account,
			},
			linkCategories: appConfig.env.msrcConfig.linkCategories,
			links: appConfig.env.msrcConfig.links,
		};

		const loginButtonOptions: LoginButtonWidgetProps = {
			language,
			theme: 'gesundheitsplattform',
			size: bigButton ? 'large' : 'small',
			loginReturnTo: migrosLoginReturnToURL,
			logoutReturnTo: migrosLogoutReturnToURL,
		};

		!showLoginButton
			? window.msrc.components.login.profileFlyout(`#${this.state.loginWidgetUID}`, loginProfileOptions)
			: window.msrc.components.login.button(`#${this.state.loginWidgetUID}`, loginButtonOptions);
	}

	public setupLogin() {
		const { setupLoginFlow } = this.props;

		// Listen to login events (subscribe to the events before triggering the login setup)
		if (setupLoginFlow) {
			(async () => {
				(await window.msrc.messenger.getInstance()).subscribe(
					'login:authenticate',
					async ({ isManualLogin, loggedIn, error }) => {
						if (loggedIn) {
							const user = await window.msrc.utilities.login.getUser();
							if (isManualLogin) {
								this.onMuiLoginLoggedIn(user);
							} else {
								this.onMuiLoginAuthenticated(user);
							}
						} else {
							this.onMuiLoginAnonymous();
						}
					}
				);

				// Initialize the login button widget
				this.initLoginButton();
			})();
		} else {
			// Initialize the login button widget
			this.initLoginButton();
		}
	}

	public componentDidMount() {
		this.setupLogin();
	}

	/*
	 * Needed for sticky teaser in { UI } part
	 * @see https:// jira.migros.net/browse/PULS-3374
	 */
	public fireStickyTeaserCustomEvent(isLoggedIn: boolean, msrcLoginData?: MSRCLoginData) {
		let user: CustomEventUser | null = null;

		// msrcLoginData.profile can be undefined if there's no session storage: migros.oicd
		if (msrcLoginData?.profile) {
			user = {
				birthdate: msrcLoginData.profile.birthdate,
				gender: msrcLoginData.profile.gender,
			};
		}

		document.dispatchEvent(
			new CustomEvent('coach-authentication-state', {
				detail: {
					isLoggedIn,
					user,
				},
			})
		);
	}

	/*
	 * Will be executed if a { user } was logged in by the widget
	 * @see http://react-components.prod-migros.ch
	 * @param { MSRCLoginData } msrcLoginData
	 */
	public onMuiLoginLoggedIn = (msrcLoginData: MSRCLoginData) => {
		const { doChangeMigrosLoginStatus, doClearRedirectRoutePath }: LoginButtonProps = this.props;
		doChangeMigrosLoginStatus(migrosLoginStatuses.LOGGEDIN);
		doClearRedirectRoutePath();
		this.fireStickyTeaserCustomEvent(true, msrcLoginData);
	};

	/*
	 * Will be executed when a user is authenticated by the M - API
	 * @param { MSRCLoginData } msrcLoginData
	 */
	public onMuiLoginAuthenticated = (msrcLoginData: MSRCLoginData) => {
		const { doChangeMigrosLoginStatus, doClearRedirectRoutePath }: LoginButtonProps = this.props;
		doChangeMigrosLoginStatus(migrosLoginStatuses.AUTHENTICATED);
		doClearRedirectRoutePath();
		this.fireStickyTeaserCustomEvent(true, msrcLoginData);
	};

	/*
	 * Will be executed when a user is logged in as anonymous
	 * @see https:// components.migros.ch/widgets/login.html
	 * @param { Object } event
	 * @param { boolean } autoLoginAttempted
	 */
	public onMuiLoginAnonymous = () => {
		const { doChangeMigrosLoginStatus }: LoginButtonProps = this.props;
		doChangeMigrosLoginStatus(migrosLoginStatuses.ANONYMOUS);
		this.fireStickyTeaserCustomEvent(false);
	};

	/*
	 * Will be executed if something { went } wrong inside the login process
	 * or inside the widget or on the M - API side.
	 * @see https:// components.migros.ch/widgets/login.html
	 * @param { Object } event
	 * @param { string } type
	 */
	public onMuiLoginError = (/* event: Object, type: string*/) => {
		const { doChangeMigrosLoginStatus, doClearRedirectRoutePath }: LoginButtonProps = this.props;

		doChangeMigrosLoginStatus(migrosLoginStatuses.LOGGEDOUT);
		doClearRedirectRoutePath();

		this.fireStickyTeaserCustomEvent(false);
	};

	public render() {
		return <div id={`${this.state.loginWidgetUID}`} />;
	}
}
